import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { billingAPI, authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Check, X, ArrowRight, Phone, Zap, Bot, Headphones,
  Crown, ChevronLeft, Cpu, Sparkles, Wallet, Shield, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

export default function PricingPage() {
  const { user, setToken, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  const isGoogleSignup = searchParams.get('source') === 'google';
  const googleData = isGoogleSignup ? JSON.parse(localStorage.getItem('google_pending') || '{}') : null;

  useEffect(() => {
    billingAPI.plans().then(r => setPlans(r.data)).catch(() => {});
  }, []);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleGetStarted = async (planId) => {
    // Existing users - redirect to dashboard billing
    if (user) {
      navigate('/dashboard/settings?tab=billing');
      return;
    }

    setSelectedPlan(planId);
    const plan = plans.find(p => p.id === planId);

    // Free plan - direct registration
    if (planId === 'free') {
      if (isGoogleSignup && googleData?.email) {
        // Complete Google registration with free plan
        setProcessing(true);
        try {
          const res = await authAPI.completeGoogleRegistration({
            google_data: googleData,
            plan: planId
          });
          localStorage.setItem('dotcall_token', res.data.token);
          localStorage.setItem('dotcall_user', JSON.stringify(res.data.user));
          localStorage.removeItem('google_pending');
          toast.success('Account created successfully!');
          navigate('/dashboard');
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Registration failed');
        } finally {
          setProcessing(false);
        }
      } else {
        // Regular registration flow
        navigate(`/register?plan=${planId}`);
      }
      return;
    }

    // All paid plans (Standard, Premium, Enterprise) - trigger Razorpay payment
    await handlePaidPlanCheckout(planId, plan);
  };

  const handlePaidPlanCheckout = async (planId, plan) => {
    setProcessing(true);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Payment gateway failed to load. Please try again.');
        setProcessing(false);
        return;
      }

      // Prepare registration data
      let orderData;
      if (isGoogleSignup && googleData?.email) {
        // Google OAuth user
        orderData = {
          plan_id: planId,
          email: googleData.email,
          name: googleData.name || '',
          company: '',
          phone: ''
        };
      } else {
        // Regular user - we need to collect details first
        // Redirect to register page with plan, let them fill details, then come back here
        navigate(`/register?plan=${planId}&require_payment=true`);
        setProcessing(false);
        return;
      }

      // Create Razorpay order
      const orderRes = await billingAPI.createRegistrationOrder(orderData);

      // Handle demo mode or free plans
      if (orderRes.data.free || orderRes.data.demo_mode) {
        if (isGoogleSignup) {
          const res = await authAPI.completeGoogleRegistration({
            google_data: googleData,
            plan: planId
          });
          localStorage.setItem('dotcall_token', res.data.token);
          localStorage.setItem('dotcall_user', JSON.stringify(res.data.user));
          localStorage.removeItem('google_pending');
          toast.success('Account created successfully!');
          navigate('/dashboard');
        } else {
          navigate(`/register?plan=${planId}`);
        }
        return;
      }

      // Open Razorpay checkout
      const options = {
        key: orderRes.data.key_id,
        amount: orderRes.data.amount,
        currency: orderRes.data.currency,
        name: 'DotCall',
        description: `${plan.name} Plan Subscription`,
        order_id: orderRes.data.id,
        handler: async (response) => {
          // Payment successful - complete registration
          await handlePaymentSuccess(response, planId, orderData);
        },
        prefill: {
          email: orderData.email,
          name: orderData.name
        },
        theme: {
          color: '#d97706'
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
            setSelectedPlan(null);
            toast.error('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment initialization failed');
      setProcessing(false);
      setSelectedPlan(null);
    }
  };

  const handlePaymentSuccess = async (razorpayResponse, planId, orderData) => {
    try {
      if (isGoogleSignup && googleData?.email) {
        // For Google users: verify payment + complete registration in one go
        const res = await authAPI.completeGoogleRegistration({
          google_data: googleData,
          plan: planId,
          payment_data: {
            razorpay_order_id: razorpayResponse.razorpay_order_id,
            razorpay_payment_id: razorpayResponse.razorpay_payment_id,
            razorpay_signature: razorpayResponse.razorpay_signature
          }
        });
        
        localStorage.setItem('dotcall_token', res.data.token);
        localStorage.setItem('dotcall_user', JSON.stringify(res.data.user));
        localStorage.removeItem('google_pending');
        toast.success('Payment successful! Welcome to DotCall');
        navigate('/dashboard');
      } else {
        // For regular users: store payment data and redirect to complete registration
        localStorage.setItem('registration_payment', JSON.stringify({
          razorpay_order_id: razorpayResponse.razorpay_order_id,
          razorpay_payment_id: razorpayResponse.razorpay_payment_id,
          razorpay_signature: razorpayResponse.razorpay_signature
        }));
        toast.success('Payment successful! Complete your registration');
        navigate(`/register?plan=${planId}&payment_success=true`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration completion failed');
    } finally {
      setProcessing(false);
      setSelectedPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] relative overflow-hidden" data-testid="pricing-page">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-amber-600/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-amber-600/5 rounded-full blur-[100px]" />
      </div>

      <header className="relative z-10 border-b border-white/5 backdrop-blur-md bg-[#020617]/80">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="pricing-back-btn">
            <ChevronLeft className="w-4 h-4" />
            <div className="flex items-center gap-2">
              <img src="https://i.ibb.co/ZRpFcYDK/oie-s-XZ9-BUTHM5-Vz.png" alt="DotCall" className="h-7 w-auto" />
            </div>
          </button>
          <div className="flex items-center gap-3">
            {user ? (
              <Button onClick={() => navigate('/dashboard')} className="bg-amber-600 hover:bg-amber-700 text-white text-sm" data-testid="pricing-dashboard-btn">Dashboard</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-300 hover:text-white text-sm" data-testid="pricing-login-btn">Login</Button>
                <Button onClick={() => navigate('/register')} className="bg-amber-600 hover:bg-amber-700 text-white text-sm" data-testid="pricing-register-btn">Get Started</Button>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-16 pb-10 text-center">
        <Badge className="bg-amber-600/15 text-amber-300 border-amber-500/30 mb-6 text-sm px-4 py-1.5">Simple, transparent pricing</Badge>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] text-white mb-4 leading-tight">
          Choose your<br />voice quality
        </h1>
        <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto mb-8">
          Start free. Scale with AI-powered calls. Go enterprise for full human-feel conversations.
        </p>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, i) => {
            const isEnterprise = plan.id === 'enterprise';
            const isPremium = plan.id === 'premium';
            const isStandard = plan.id === 'standard';
            const isFree = plan.id === 'free';

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl border backdrop-blur-sm p-6 flex flex-col transition-all duration-300 hover:scale-[1.02] hover:shadow-xl animate-fade-in ${
                  isPremium ? 'border-amber-500/40 bg-gradient-to-b from-amber-600/20 to-amber-700/10 ring-2 ring-amber-500/50 shadow-[0_0_30px_rgba(245,158,11,0.15)] lg:-translate-y-2'
                  : isStandard ? 'border-emerald-500/40 bg-gradient-to-b from-emerald-600/15 to-emerald-700/5 ring-1 ring-emerald-500/30'
                  : isEnterprise ? 'border-amber-500/30 bg-gradient-to-b from-amber-600/15 to-amber-700/5'
                  : 'border-slate-500/30 bg-gradient-to-b from-slate-600/20 to-slate-700/10'
                }`}
                style={{ animationDelay: `${i * 100}ms` }}
                data-testid={`pricing-card-${plan.id}`}
              >
                {isPremium && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-amber-600 text-white border-0 text-xs px-3 py-1">Most Popular</Badge>
                  </div>
                )}
                {isStandard && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-emerald-600 text-white border-0 text-xs px-3 py-1">Best Value</Badge>
                  </div>
                )}

                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center ${
                    isFree ? 'text-slate-300' : isStandard ? 'text-emerald-300' : isPremium ? 'text-amber-300' : 'text-amber-300'
                  }`}>
                    {isFree ? <Zap className="w-4 h-4" /> : isStandard ? <Bot className="w-4 h-4" /> : isPremium ? <Cpu className="w-4 h-4" /> : <Crown className="w-4 h-4" />}
                  </div>
                  <h3 className="text-white font-semibold font-['Outfit'] text-lg">{plan.name}</h3>
                </div>

                <Badge className={`text-[10px] w-fit mb-3 ${
                  isStandard ? 'bg-emerald-600/20 text-emerald-300 border-emerald-500/30' 
                  : isEnterprise ? 'bg-amber-600/20 text-amber-300 border-amber-500/30' 
                  : 'bg-amber-600/20 text-amber-300 border-amber-500/30'
                }`}>
                  <Sparkles className="w-3 h-3 mr-1" />
                  {isFree ? 'Quick Demo' : 'All Indian Languages'}
                </Badge>

                <div className="mb-5">
                  {isEnterprise ? (
                    <>
                      <div className="text-xl font-bold font-['Outfit'] text-amber-300 mb-1">{plan.display_price}</div>
                      <p className="text-xs text-slate-400">{plan.display_desc}</p>
                    </>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold font-['Outfit'] text-white">
                        {plan.price === 0 ? 'Free' : `₹${plan.price.toLocaleString('en-IN')}`}
                      </span>
                      {plan.price > 0 && <span className="text-xs text-slate-500">/month</span>}
                    </div>
                  )}
                  {isStandard && <p className="text-xs text-emerald-300/70 mt-1">Calls from ₹{plan.display_rate}/min</p>}
                  {isPremium && <p className="text-xs text-amber-300/70 mt-1">Calls from ₹{plan.display_rate}/min · All Indian languages</p>}
                  {isEnterprise && <p className="text-xs text-amber-300/70 mt-1">Calls from ₹{plan.display_rate}/min</p>}
                </div>

                <div className="space-y-2.5 mb-5 pb-5 border-b border-white/5">
                  <div className="flex items-center gap-2.5 text-xs">
                    <Bot className={`w-3.5 h-3.5 flex-shrink-0 ${isEnterprise ? 'text-amber-300' : isPremium ? 'text-amber-300' : isStandard ? 'text-emerald-300' : 'text-slate-300'}`} />
                    <span className="text-slate-300">{plan.agents === -1 ? 'Unlimited' : plan.agents} Agent{plan.agents !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-xs">
                    <Headphones className={`w-3.5 h-3.5 flex-shrink-0 ${isEnterprise ? 'text-amber-300' : isPremium ? 'text-amber-300' : isStandard ? 'text-emerald-300' : 'text-slate-300'}`} />
                    <span className="text-slate-300">
                      {isEnterprise ? 'Pay as you go' : `${plan.minutes} min ${isFree ? '(lifetime)' : '/month'}`}
                    </span>
                  </div>
                  {(isPremium || isEnterprise || isStandard) && (
                    <div className="flex items-center gap-2.5 text-xs">
                      <Wallet className={`w-3.5 h-3.5 flex-shrink-0 ${isEnterprise ? 'text-amber-300' : isStandard ? 'text-emerald-300' : 'text-amber-300'}`} />
                      <span className="text-slate-300">Wallet {isEnterprise ? '(min ₹6,999)' : 'for overage'}</span>
                    </div>
                  )}
                  {isFree && (
                    <div className="flex items-center gap-2.5 text-xs">
                      <X className="w-3.5 h-3.5 flex-shrink-0 text-red-400" />
                      <span className="text-red-300/80">No extra usage</span>
                    </div>
                  )}
                </div>

                <ul className="space-y-2 mb-5 flex-1">
                  {plan.features.filter((_, fi) => fi >= 3).map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-xs text-slate-400">
                      <Check className="w-3 h-3 text-emerald-400/70 flex-shrink-0 mt-0.5" /> 
                      <span className="leading-tight">{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleGetStarted(plan.id)}
                  disabled={processing && selectedPlan === plan.id}
                  data-testid={`pricing-select-${plan.id}`}
                  className={`w-full group text-sm py-4 ${
                    isPremium ? 'bg-amber-600 hover:bg-amber-700 text-white shadow-[0_0_20px_rgba(245,158,11,0.3)]'
                    : isStandard ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-[0_0_20px_rgba(16,185,129,0.3)]'
                    : isEnterprise ? 'bg-amber-600/80 hover:bg-amber-600 text-white'
                    : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
                  }`}
                >
                  {processing && selectedPlan === plan.id ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</>
                  ) : (
                    <>{isFree ? 'Start Free' : isEnterprise ? 'Get Enterprise' : 'Get Started'}
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" /></>
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Info boxes */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-900/10 backdrop-blur-sm p-6">
            <div className="flex items-center gap-2 mb-3">
              <Bot className="w-5 h-5 text-emerald-400" />
              <h3 className="text-sm font-semibold font-['Outfit'] text-white">Standard — ₹8/min</h3>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              180 minutes/month with all Indian languages. Best value for
              outbound campaigns and everyday conversations.
            </p>
          </div>
          <div className="rounded-xl border border-amber-500/20 bg-amber-900/10 backdrop-blur-sm p-6">
            <div className="flex items-center gap-2 mb-3">
              <Cpu className="w-5 h-5 text-amber-400" />
              <h3 className="text-sm font-semibold font-['Outfit'] text-white">Premium — ₹6.5/min</h3>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              650 minutes/month with all Indian languages. Priority support
              and phone number management for scaling teams.
            </p>
          </div>
          <div className="rounded-xl border border-amber-500/20 bg-amber-900/10 backdrop-blur-sm p-6">
            <div className="flex items-center gap-2 mb-3">
              <Crown className="w-5 h-5 text-amber-400" />
              <h3 className="text-sm font-semibold font-['Outfit'] text-white">Enterprise — ₹5.5/min</h3>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Pay as you go with lowest per-minute rate. 20 agents, dedicated support,
              CRM & e-commerce integrations. Grok Voice AI powered.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
